import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { UNSAFE_NavigationContext } from 'react-router-dom';
import type { To } from 'react-router-dom';

/**
 * POS-I224: leave guard for pages with unsaved edits.
 * - Browser: while `dirty` is true the browser asks "Leave site?" on reload, close and typed URLs.
 * - In-app: BrowserRouter has no useBlocker, so while `dirty` is true the router's navigator
 *   push/replace are held when they would change the page (same-path query changes pass).
 *   The page renders a confirm dialog from `leaveGuard`: `confirmLeave` replays the held
 *   navigation, `stay` drops it.
 */
export interface LeaveGuard {
  /** True while a navigation is held and the confirm dialog should be open. */
  open: boolean;
  confirmLeave: () => void;
  stay: () => void;
}

type NavFn = (to: To, state?: unknown, opts?: unknown) => void;
interface MutableNavigator { push: NavFn; replace: NavFn }

export function targetPath(to: To, base: string = window.location.href): string {
  const raw = typeof to === 'string' ? to : `${to.pathname ?? ''}${to.search ?? ''}`;
  try {
    return new URL(raw, base).pathname;
  } catch {
    return raw;
  }
}

export function useUnsavedChanges(dirty: boolean): LeaveGuard {
  const { navigator } = useContext(UNSAFE_NavigationContext);
  const [open, setOpen] = useState(false);
  const held = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (!dirty) return undefined;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  useEffect(() => {
    if (!dirty) return undefined;
    const nav = navigator as unknown as MutableNavigator;
    const { push, replace } = nav;
    const guard = (original: NavFn): NavFn => (to, state, opts) => {
      if (targetPath(to) === window.location.pathname) {
        original.call(nav, to, state, opts);
        return;
      }
      held.current = () => original.call(nav, to, state, opts);
      setOpen(true);
    };
    nav.push = guard(push);
    nav.replace = guard(replace);
    return () => {
      nav.push = push;
      nav.replace = replace;
    };
  }, [dirty, navigator]);

  const confirmLeave = useCallback(() => {
    const go = held.current;
    held.current = null;
    setOpen(false);
    // The guard is removed on the next render (dirty stays true until then), so replay on the
    // original functions: the held closure already calls them.
    go?.();
  }, []);
  const stay = useCallback(() => {
    held.current = null;
    setOpen(false);
  }, []);

  return { open, confirmLeave, stay };
}
